import styled, { css } from "styled-components";

import Text from "@/components/Text";
import { FC } from "react";
import { hexToRgba } from "@/utils/theme";

interface ProgressProps {
  $progress?: string;
  $color?: string;
  $type?: string;
}

const Progress = styled.div<ProgressProps>`
  height: 100%;
  width: ${({ $progress }) => $progress};
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 10px;
  max-width: 100%;

  ${({ $color, theme }) =>
    $color === "primary" &&
    css`
      background: ${theme.colors.purple};
    `}

  ${({ $color, theme }) =>
    $color === "success" &&
    css`
      background: ${theme.colors.success};
    `}

  ${({ $color, theme, $type }) =>
    $color === "success" &&
    $type === "big" &&
    css`
      background: ${hexToRgba(theme.colors.success, 0.5)};
    `}

  ${({ $color, theme }) =>
    $color === "failed" &&
    css`
      background: ${theme.colors.error};
    `}

  ${({ $color, theme, $type }) =>
    $color === "failed" &&
    $type === "big" &&
    css`
      background: ${hexToRgba(theme.colors.error, 0.5)};
    `}
  
  ${({ $color, theme }) =>
    $color === "progress" &&
    css`
      background: ${theme.colors.warning};
    `}

  ${({ $color, theme, $type }) =>
    $color === "progress" &&
    $type === "big" &&
    css`
      background: ${hexToRgba(theme.colors.warning, 0.5)};
    `}
`;

interface ProgressHolderProps {
  $type?: string;
  $color?: string;
}

const ProgressHolder = styled.div<ProgressHolderProps>`
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.lightGray};
  height: 3px;
  ${({ $type, theme }) =>
    $type === "big" &&
    css`
      height: 55px;
      background: ${theme.colors.grayscaleLight};
      border-radius: 4px;
      ${Progress} {
        border-radius: 4px;
      }
    `}
  ${({ $type, theme, $color }) =>
    $type === "big" &&
    $color === "success" &&
    css`
      background: ${theme.colors.successLight};
    `}
  ${({ $type, theme, $color }) =>
    $type === "big" &&
    $color === "failed" &&
    css`
      background: ${theme.colors.errorLight};
    `}
    ${({ $type, theme, $color }) =>
    $type === "big" &&
    $color === "progress" &&
    css`
      background: ${theme.colors.warningLight};
    `}
`;

const ProgressText = styled(Text)`
  margin-bottom: 8px;
`;

interface Props {
  progress: number;
  type?: "big" | "default";
  color?: "success" | "progress" | "failed" | "primary";
  showPercentage?: boolean;
  className?: string;
}

const ProgressBar: FC<Props> = ({
  progress = 0,
  type = "default",
  color = "primary",
  showPercentage = false,
  className,
}) => {
  return (
    <>
      {showPercentage && (
        <ProgressText $type="extraSmall">{Math.round(progress)} %</ProgressText>
      )}
      <ProgressHolder $color={color} $type={type} className={className}>
        <Progress $type={type} $color={color} $progress={`${progress}%`} />
      </ProgressHolder>
    </>
  );
};

export default ProgressBar;
